import classnames from "classnames";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import DecorativeBorderHorizontal from "../../public/svg/decorative-border-horizontal-long.svg";
import Logo from "../../public/svg/sarkanniemi-logo.svg";
import replaceHTMLEntitiesFromText from "../lib/replace-html-entities";

import css from "./PrintableSeasonCard.module.scss";
import PrintableSeasonCardPhysical from "./PrintableSeasonCardPhysical";

export const TEST_ID_TITLE = "printable-season-card-title";
export const TEST_ID_HOLDER_PHOTO = "printable-season-card-holder-photo";
export const TEST_ID_QR_CODE = "printable-season-card-qr-code";
export const TEST_ID_HOLDER_NAME = "printable-season-card-holder-name";
export const TEST_ID_BACKSIDE_TITLE_LEFT =
  "printable-season-card-backside-title-left";
export const TEST_ID_BACKSIDE_TITLE_RIGHT =
  "printable-season-card-backside-title-right";
export const TEST_ID_TERMS_AND_CONDITIONS =
  "printable-season-card-terms-and-conditions";
export const TEST_ID_SEASON_CARD_PAGE_LINK = "printable-season-card-page-link";
export const TEST_ID_COMPANY_INFORMATION =
  "printable-season-card-company-information";
export const TEST_ID_EMPTY_BOX = "printable-season-card-empty-box";

interface PrintableSeasonCardProps {
  name: string;
  photoURL: string;
  print?: "card";
  qrCodeURL: string;
}

const PrintableSeasonCard: React.FC<PrintableSeasonCardProps> = ({
  name,
  photoURL,
  print,
  qrCodeURL,
}) => {
  const { formatMessage, locale } = useIntl();

  if (print === "card") {
    return (
      <PrintableSeasonCardPhysical
        name={name}
        photoURL={photoURL}
        qrCodeURL={qrCodeURL}
      />
    );
  }

  const seasonCardPagePath = `${locale}/${formatMessage({
    id: "appPath.seasonCardPage",
  })}`;

  return (
    <React.Fragment>
      <div className={css.container}>
        <DecorativeBorderHorizontal className={css.topGraphic} />
        <div className={css.contentContainer}>
          <div className={css.information}>
            <div className={css.titleContainer}>
              <h1 className={css.title} data-testid={TEST_ID_TITLE}>
                {replaceHTMLEntitiesFromText(
                  formatMessage({ id: "seasonCardPage.title" }),
                )}
              </h1>
            </div>
            <div className={css.photoContainer}>
              <div className="aspect-ratio aspect-ratio--1x1">
                <picture>
                  <img
                    className="aspect-ratio--object ofi-cover ofi--top w-100"
                    alt={formatMessage({ id: "seasonCardPage.holderPhotoAlt" })}
                    src={photoURL}
                    data-testid={TEST_ID_HOLDER_PHOTO}
                  />
                </picture>
              </div>
            </div>
            <div className={css.qrCodeContainer}>
              <div className="aspect-ratio aspect-ratio--1x1">
                <picture>
                  <img
                    className="aspect-ratio--object ofi-cover ofi--top w-100"
                    alt={formatMessage({ id: "seasonCardPage.qrCodeAlt" })}
                    src={qrCodeURL}
                    data-testid={TEST_ID_QR_CODE}
                  />
                </picture>
              </div>
            </div>
            <div
              className={classnames(css.nameContainer, css.nameContainerMobile)}
            >
              <span data-testid={TEST_ID_HOLDER_NAME}>{name}</span>
            </div>
          </div>
          <div className={css.nameContainer}>
            <span data-testid={TEST_ID_HOLDER_NAME}>{name}</span>
          </div>
        </div>
        <div className={css.track}>
          <div className={css.logoContainer}>
            <Logo />
          </div>
          <div className={css.bottomGraphic} />
        </div>
      </div>

      <div className={classnames(css.container, css.containerBackside)}>
        <div className={css.circleContainer}>
          <span
            className={classnames(css.backsideTitleLeft, {
              [css.en]: locale === "en",
            })}
            data-testid={TEST_ID_BACKSIDE_TITLE_LEFT}
          >
            <FormattedMessage id="seasonCardPage.backsideTitleLeft" />
          </span>
          <span
            className={classnames(css.backsideTitleRight, {
              [css.en]: locale === "en",
            })}
            data-testid={TEST_ID_BACKSIDE_TITLE_RIGHT}
          >
            <FormattedMessage id="seasonCardPage.backsideTitleRight" />
          </span>
          <div className={classnames(css.circle, css.circleSolid)} />
        </div>
        <div
          className={classnames(
            css.contentContainer,
            css.contentContainerBackside,
          )}
        >
          <div
            className={css.backsideText}
            data-testid={TEST_ID_TERMS_AND_CONDITIONS}
          >
            <p>
              <FormattedMessage id="seasonCardPage.backsideText1" />
            </p>
            <p>
              <FormattedMessage id="seasonCardPage.backsideText2" />
            </p>
            <p className={css.highlightText}>
              <a
                href={`/${seasonCardPagePath}`}
                target="_blank"
                rel="noreferrer"
                data-testid={TEST_ID_SEASON_CARD_PAGE_LINK}
              >
                {`${location.host}/${seasonCardPagePath}`}
              </a>
            </p>
          </div>
        </div>
        <div className={classnames(css.track, css.trackBackside)}>
          <div
            className={classnames(css.logoContainer, css.logoContainerBackside)}
          >
            <Logo />
          </div>
          <div className={css.bottomGraphic} />
          <div className={css.companyInformationContainer}>
            <ul
              className={css.companyInformation}
              data-testid={TEST_ID_COMPANY_INFORMATION}
            >
              <li>
                <FormattedMessage id="seasonCardPage.companyName" />
              </li>
              <li>
                <FormattedMessage id="seasonCardPage.companyPhone" />
              </li>
              <li>
                <FormattedMessage id="seasonCardPage.companyWebsite" />
              </li>
            </ul>
            <div className={css.emptyBox} data-testid={TEST_ID_EMPTY_BOX} />
          </div>
        </div>
        <style>{`
          html {
            font-size: 1vw;
          }

          body {
            background-color: #525659;
            margin: 1rem;
            padding: 0;
          }

          html,
          body,
          #__next {
            height: auto;
          }

          @media screen and (max-width: 500px) {
            html,
            body,
            #__next {
              height: 100%;
            }
          }

          @media print {
            * {
              -webkit-print-color-adjust: exact;
              color-adjust: exact;
            }

            html {
              font-size: 1mm;
            }

            body {
              background: #fff;
            }
          }
        `}</style>
      </div>
    </React.Fragment>
  );
};

export default PrintableSeasonCard;
